import {ReactNode, Suspense, useContext, useEffect} from 'react'
import {safeLocalStorage} from "../util/safeStorage"
import {Navigate, useLocation} from 'react-router-dom'
import {ErrorBoundary} from 'react-error-boundary'
import {AuthContext} from "../context/AuthContext.tsx";
import {Loader} from "../components/Loader.tsx";
import {ErrorState} from "../components/ErrorState.tsx";
import {menuItems} from "../components/menuItems.tsx";
import {LayoutWrapperCandidate as OldWrapper} from "../components/LayoutWrapperCandidate_PRE_RELEASE.tsx";
import {LayoutWrapperCandidate} from "../components/LayoutWrapperCandidate.tsx";

const USE_NEW_LAYOUT = true;

type Props = {
    permissions?: string[]
    roles?: string[]
    redirectTo?: string
    children: ReactNode
}

export const PrivateRoute = (props: Props) => {
    const {redirectTo = '/login', children} = props
    const location = useLocation()
    const {isAuthenticated, user, loadingUserData, signOut} = useContext(AuthContext)

    const hasAllPermissions = !!user

    useEffect(() => {
        // Save the current path when component mounts or path changes
        if (location.pathname !== '/' && location.pathname !== '/login') {
            safeLocalStorage.setItem('appPredefinedRedirectPath', location.pathname + location.search);
        }
    }, [location.pathname, location.search]);

    if (loadingUserData) {
        return <Loader/>
    }

    if (!isAuthenticated) {
        const redirectPath = location.pathname + location.search;
        safeLocalStorage.setItem('appPredefinedRedirectPath', redirectPath);

        // Include the redirect path in the URL query parameters
        const loginPath = `${redirectTo}?from=${encodeURIComponent(redirectPath)}`;

        return <Navigate to={loginPath} state={{ from: location }} replace />
    }

    if (!hasAllPermissions) {
        // If user object is missing or permissions are insufficient,
        // send the user to the login page so auth can be re-established
        // instead of redirecting to root which caused a redirect loop.
        return <Navigate to={redirectTo} replace />
    }

    const LayoutWrapper = USE_NEW_LAYOUT ? LayoutWrapperCandidate : OldWrapper

    return (
        <ErrorBoundary
            fallback={<ErrorState/>}
        >
            <Suspense fallback={<Loader/>}>
                <LayoutWrapper menuItems={menuItems} signOut={signOut}>{children}</LayoutWrapper>
            </Suspense>
        </ErrorBoundary>
    )
}
